/**
 * Digital resources offered on the portal, and opening one signed in.
 */

import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { portalFetch, usePatronAuth } from "../contexts/PatronAuthContext";

/** Whether the visitor can open the resource (decided by the server). */
export type DigitalAccess = "available" | "login_required" | "not_allowed" | "account_inactive";

export interface PortalDigitalResource {
  id: string;
  provider: string;
  name: string;
  description: string | null;
  category: string;
  logoUrl: string | null;
  access: DigitalAccess;
}

interface ListResponse {
  resources: PortalDigitalResource[];
}

interface AccessResponse {
  url?: string;
  error?: string;
}

export function usePortalDigitalResources() {
  const { isAuthenticated } = usePatronAuth();

  return useQuery({
    // Eligibility depends on who is signed in
    queryKey: ["portal", "digital-resources", isAuthenticated],
    queryFn: async (): Promise<PortalDigitalResource[]> => {
      const response = await portalFetch("/digital-resources");
      if (!response.ok) throw new Error("Impossible de charger les ressources numériques.");
      return ((await response.json()) as ListResponse).resources;
    },
  });
}

/**
 * Opens a resource in a new tab, already signed in.
 *
 * The tab is opened synchronously on click, before asking the server for the
 * signed link: opening it after the request would be blocked as a pop-up.
 */
export function useOpenDigitalResource() {
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<{ id: string; message: string } | null>(null);

  const open = useCallback(
    async (resource: Pick<PortalDigitalResource, "id" | "name">, recordId?: string) => {
      setError(null);
      setPendingId(resource.id);

      const tab = window.open("", "_blank");
      if (tab) {
        // The provider's page must not be able to script the portal
        tab.opener = null;
        tab.document.title = `Connexion à ${resource.name}…`;
        tab.document.body.textContent = `Connexion à ${resource.name}…`;
      }

      try {
        const response = await portalFetch(`/digital-resources/${resource.id}/access`, {
          method: "POST",
          body: JSON.stringify(recordId ? { recordId } : {}),
        });
        const data = (await response.json().catch(() => ({}))) as AccessResponse;

        if (!response.ok || !data.url) {
          throw new Error(
            response.status === 401
              ? "Votre session a expiré : reconnectez-vous."
              : (data.error ?? "La connexion au service a échoué. Réessayez plus tard.")
          );
        }

        if (tab) {
          tab.location.replace(data.url);
        } else {
          // Pop-ups blocked: open it in this tab instead
          window.location.assign(data.url);
        }
      } catch (e) {
        tab?.close();
        setError({
          id: resource.id,
          message: e instanceof Error ? e.message : "La connexion au service a échoué.",
        });
      } finally {
        setPendingId(null);
      }
    },
    []
  );

  return { open, pendingId, error };
}
